import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8bsd1rh5tg6socjsm0";

const CONTACT_ITEMS = [
  { icon: "ri-mail-line", label: "内容反馈邮箱", value: "contact@kr-eth-drama.demo" },
  { icon: "ri-time-line", label: "反馈处理时间", value: "工作日 09:00 - 18:00" },
  { icon: "ri-map-pin-line", label: "运营团队", value: "韩国伦理在线观看编辑组（演示）" },
];

export default function About() {
  usePageMeta(
    "关于我们 | 韩国伦理在线观看",
    "了解韩国伦理在线观看的网站定位、内容说明、法律声明与联系方式，并可通过反馈表单与我们取得联系。",
  );

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setError("");
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete("website_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setSubmitting(true);
    setStatus("idle");
    setError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const lower = String(serverMsg).toLowerCase();
      const isSpam = lower.includes("spam");

      if (!response.ok || (code && code !== "OK") || isSpam) {
        setStatus("error");
        setError(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setError("");
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus("error");
      setError("网络异常，提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-8 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关于我们 - 韩国伦理在线观看
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
            我们是一个专注于韩国伦理题材影视作品的在线展示与浏览平台，致力于帮助成年观众更高效地发现、筛选和了解优质的伦理电影与电视剧。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-10 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* intro + legal */}
          <div className="space-y-8 lg:col-span-2">
            <section className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-compass-3-line text-xl text-accent-500" />
                网站介绍与定位
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                韩国伦理在线观看聚焦韩国伦理题材电影与电视剧，以现代门户风格的界面呈现作品封面、简介、演职人员与相关信息。我们通过多维筛选、专题合集与全文搜索，帮助用户快速定位想看的内容，并通过本地收藏功能记录个人心愿单。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {[
                  { icon: "ri-movie-2-line", title: "作品信息", desc: "封面 / 简介 / 演职人员" },
                  { icon: "ri-filter-3-line", title: "多维筛选", desc: "题材 / 年份 / 热度" },
                  { icon: "ri-search-line", title: "全文搜索", desc: "片名 / 导演 / 演员" },
                ].map((item) => (
                  <div key={item.title} className="rounded-md bg-background-100 p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                      <i className={item.icon} />
                    </span>
                    <h3 className="mt-3 text-sm font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-1 text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-shield-check-line text-xl text-accent-500" />
                法律声明与免责说明
              </h2>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                  本站为演示性质的影视信息展示站点，所展示的作品名称、海报与简介均为演示数据，不代表任何真实影视作品。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                  本站不提供任何视频播放、下载或付费服务，播放相关入口仅为占位提示。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                  本站内容仅面向成年观众，请自觉遵守所在地区的法律法规，理性、健康地观影。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base text-primary-500" />
                  收藏功能仅保存在你的浏览器本地，不会上传任何个人数据。
                </li>
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-contacts-book-line text-xl text-accent-500" />
                联系方式
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {CONTACT_ITEMS.map((item) => (
                  <div key={item.label} className="rounded-md bg-background-100 p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-600">
                      <i className={item.icon} />
                    </span>
                    <p className="mt-3 text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-1 break-all text-sm font-semibold text-foreground-950">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* feedback form */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-feedback-line text-xl text-accent-500" />
                意见反馈
              </h2>
              <p className="mt-2 text-xs text-foreground-500">
                有内容纠错、功能建议或合作意向？填写下方表单告诉我们。
              </p>

              <form
                id="about-feedback-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 space-y-4"
              >
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    你的称呼 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入称呼"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div>
                  <label htmlFor="fb-subject" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    反馈类型 <span className="text-accent-600">*</span>
                  </label>
                  <select
                    id="fb-subject"
                    name="subject"
                    required
                    defaultValue=""
                    className="h-10 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="" disabled>
                      请选择反馈类型
                    </option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="功能建议">功能建议</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="fb-message" className="mb-1.5 block text-xs font-medium text-foreground-700">
                    详细内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="请描述你的问题或建议（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  <p className="mt-1 text-right text-[11px] text-foreground-400">
                    {messageLength}/500
                  </p>
                </div>

                <input
                  className="hp-feedback-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={submitting ? "ri-loader-4-line animate-spin text-base" : "ri-send-plane-line text-base"} />
                  {submitting ? "提交中..." : "提交反馈"}
                </button>

                {status === "success" && (
                  <div className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-xs font-medium text-accent-900">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-base text-accent-600" />
                    提交成功，感谢你的反馈！我们会尽快处理。
                  </div>
                )}
                {status === "error" && (
                  <div className="flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-xs font-medium text-secondary-900">
                    <i className="ri-error-warning-fill mt-0.5 text-base text-secondary-600" />
                    {error || "提交失败，请稍后重试。"}
                  </div>
                )}
              </form>

              <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
                提交即表示你同意我们仅将信息用于处理本次反馈。也可先
                <Link to="/search" className="mx-0.5 text-primary-600 hover:underline">
                  搜索影视
                </Link>
                了解更多作品信息。
              </p>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}